/*
 * BroadcastRoundSection — the "Team View Classic Lower Third" design from
 * Travis's 2nd handoff zip, rendered for a single round.
 *
 * Layout (top to bottom):
 *   1. Hero scoreboard bar — two angled red + blue trapezoids with team
 *      eyebrows (team name · CAPT. firstname), team name, this-round
 *      score on the right of each bar with the running event-wide total
 *      shown as a smaller subtitle under it.
 *   2. Stat strip — 4 cells on web, 3 on phone: SESSION / MATCHES / LIVE /
 *      TO CLINCH. "TO CLINCH" is the event-wide points threshold.
 *   3. Match rows — one row per match in this round. Red side | center
 *      THRU pill (+ MINE indicator if it's the signed-in player's match)
 *      | blue side. Leading side gets tinted soft background + accent
 *      border; trailing side stays plain. The margin badge ("3 UP" /
 *      "AS") sits on the leading side; "AS" appears on the blue side
 *      only when the match is tied.
 *   4. Footer — small green "live" dot + SESSION N OF M tag on the
 *      left, TO CLINCH reminder on the right.
 *
 * Color philosophy: per Travis's call, each team uses its OWN
 * primaryColor for the banner gradients and row tints. We don't hardcode
 * red/blue. The default fallbacks (red for side 1, blue for side 2) only
 * kick in when a team has no primaryColor set.
 */

import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { paletteFor, type TeamPalette } from '@/lib/teamColors';
import { deriveMatchState } from '@/lib/matchState';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';

const TOTAL_HOLES = 18;

export type SectionParticipant = {
  eventPlayerId: string;
  sideNumber: number;
  displayName: string;
  userId: string | null;
};

export type SectionMatchHole = {
  holeNumber: number;
  winnerTeamId: string | null;
  isHalved: boolean;
};

export type SectionMatch = {
  id: string;
  matchNumber: number;
  status: string;
  teamAId: string | null;
  teamBId: string | null;
  teamAPointsAwarded: number;
  teamBPointsAwarded: number;
  winningTeamId: string | null;
  finalResultDisplay: string | null;
  finalResultType: string | null;
  participants: SectionParticipant[];
  matchHoles: SectionMatchHole[];
};

export type SectionTeam = {
  id: string;
  name: string;
  primaryColor: string | null;
  /** The team's captain's first name, e.g. "Pat". Used in the hero
   *  eyebrow as "TEAM PAT · CAPT. PAT". Null if no captain assigned. */
  captainFirstName: string | null;
};

export interface BroadcastRoundSectionProps {
  /** Round identity for the hero subtitle + footer. */
  round: {
    id: string;
    name: string;
    roundNumber: number;
    /** "SESSION N OF M" footer. M = total rounds in the event. */
    totalRoundsInEvent: number;
    courseNameSnapshot: string | null;
    defaultTeeNameSnapshot: string | null;
    parTotal: number | null;
    sessionType: string | null;
    formatType: string;
  };
  teamA: SectionTeam | null;
  teamB: SectionTeam | null;
  matches: SectionMatch[];
  /** Running event-wide points. Shown as a smaller subtitle under the
   *  hero "this round" score so a captain can see both the round AND
   *  the event arc from any team view. */
  eventTotals: { teamA: number; teamB: number };
  /** Event-wide points needed to clinch — typically computed from the
   *  event's winningRuleType + total available points. */
  pointsToClinch: number | null;
  myUserId: string | null;
}

export function BroadcastRoundSection(props: BroadcastRoundSectionProps) {
  const navigate = useNavigate();
  const { round, teamA, teamB, matches, eventTotals, pointsToClinch, myUserId } = props;

  const palettes = {
    a: paletteFor(teamA?.primaryColor ?? null, 1),
    b: paletteFor(teamB?.primaryColor ?? null, 2),
  };

  // This-round point totals = sum of teamA/teamB awarded across matches.
  const roundTotals = matches.reduce(
    (acc, m) => ({
      a: acc.a + (m.teamAPointsAwarded ?? 0),
      b: acc.b + (m.teamBPointsAwarded ?? 0),
    }),
    { a: 0, b: 0 },
  );

  // Live count = matches that have at least one hole played but aren't
  // final yet. Falls through to status check when matchHoles is empty.
  const liveCount = matches.reduce((n, m) => {
    if (m.finalResultType || m.status === 'final') return n;
    if (m.status === 'in_progress') return n + 1;
    if ((m.matchHoles ?? []).some((h) => h.winnerTeamId != null || h.isHalved)) return n + 1;
    return n;
  }, 0);

  return (
    <section className="bcast-round-section">
      {/* ── Hero scoreboard ── */}
      <div className="bcast-hero-bar">
        <HeroSide
          align="left"
          palette={palettes.a}
          team={teamA}
          roundScore={roundTotals.a}
          eventScore={eventTotals.teamA}
          fallbackEyebrow="TEAM A"
        />
        <HeroSide
          align="right"
          palette={palettes.b}
          team={teamB}
          roundScore={roundTotals.b}
          eventScore={eventTotals.teamB}
          fallbackEyebrow="TEAM B"
        />
      </div>

      {/* ── Stat strip ── */}
      <div className="bcast-stat-strip-tv">
        <StatCell
          label="SESSION"
          value={formatSessionValue(round)}
          mobileText={formatSessionValue(round)}
        />
        <StatCell
          label="MATCHES"
          value={String(matches.length)}
          mobileText={`${matches.length} MATCHES`}
        />
        <StatCell
          label="LIVE"
          value={String(liveCount)}
          mobileText={`${liveCount} LIVE`}
          liveTint={liveCount > 0}
        />
        {/* TO CLINCH stays desktop-only — phone footer already shows it. */}
        <StatCell
          label="TO CLINCH"
          value={pointsToClinch != null ? formatTeamCupPoints(pointsToClinch) : '—'}
          hideOnMobile
        />
      </div>

      {/* ── Match rows ── */}
      <div className="bcast-row-list">
        {matches.length === 0 && (
          <div style={{ padding: '32px 22px', color: 'var(--bcast-ink-2)', textAlign: 'center' }}>
            No matches in this round yet. Build pairings from the round page.
          </div>
        )}
        {matches.map((m) => (
          <MatchRow
            key={m.id}
            match={m}
            palettes={palettes}
            isMine={(m.participants ?? []).some((p) => p.userId === myUserId)}
            onClick={() => navigate(`/app/tournament-matches/${m.id}/score`)}
          />
        ))}
      </div>

      {/* ── Footer ── */}
      <div className="bcast-section-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="bcast-live-dot" />
          <span className="bcast-footer-tag">
            SESSION {round.roundNumber} OF {round.totalRoundsInEvent}
          </span>
        </div>
        {pointsToClinch != null && (
          <div className="bcast-footer-clinch">
            {formatTeamCupPoints(pointsToClinch)} <span style={{ color: 'var(--bcast-ink-3)', fontWeight: 600 }}>TO CLINCH</span>
          </div>
        )}
      </div>
    </section>
  );
}

// ── HeroSide ──────────────────────────────────────────────────────────────
function HeroSide({
  align, palette, team, roundScore, eventScore, fallbackEyebrow,
}: {
  align: 'left' | 'right';
  palette: TeamPalette;
  team: SectionTeam | null;
  roundScore: number;
  eventScore: number;
  fallbackEyebrow: string;
}) {
  // Strip a leading "Team " from team.name so we don't render
  // "TEAM TEAM PAT" — the eyebrow already prepends "TEAM ".
  const rawName = team?.name ?? '';
  const cleanName = rawName.replace(/^team\s+/i, '').toUpperCase() || fallbackEyebrow;
  const captain = team?.captainFirstName;

  const gradient =
    align === 'left'
      ? `linear-gradient(95deg, ${palette.solid}, ${palette.deep})`
      : `linear-gradient(265deg, ${palette.solid}, ${palette.deep})`;

  // clip-path lives in CSS (.bcast-hero-side.left/.right) so the mobile
  // media query can shrink the 32px diagonal to 14px. Hardcoding it here
  // let the angle slice through the score digits on phones.
  const style: CSSProperties = {
    background: gradient,
  };

  const scoreBlock = (
    <div className="bcast-hero-score-block">
      <div className="bcast-hero-score bcast-tnum">{formatTeamCupPoints(roundScore)}</div>
      <div className="bcast-hero-score-sub">{formatTeamCupPoints(eventScore)} TOTAL</div>
    </div>
  );
  const textBlock = (
    <div className="bcast-hero-text-block" style={{ textAlign: align === 'right' ? 'right' : 'left' }}>
      <div className="bcast-hero-eyebrow">
        <span>TEAM {cleanName}</span>
        {captain && (
          <span className="bcast-hero-eyebrow-captain"> · CAPT. {captain.toUpperCase()}</span>
        )}
      </div>
      <div className="bcast-hero-name">{cleanName}</div>
    </div>
  );

  return (
    <div className={`bcast-hero-side ${align}`} style={style}>
      {align === 'left' ? textBlock : scoreBlock}
      {align === 'left' ? scoreBlock : textBlock}
    </div>
  );
}

// ── StatCell ──────────────────────────────────────────────────────────────
function StatCell({
  label, value, mobileText, liveTint, hideOnMobile,
}: {
  label: string;
  value: string;
  /** Single-line text shown on mobile in place of label+value. Defaults
   *  to value if not provided. */
  mobileText?: string;
  liveTint?: boolean;
  /** Skip rendering on phones entirely — used by TO CLINCH which the
   *  footer already shows on mobile. */
  hideOnMobile?: boolean;
}) {
  return (
    <div className={`bcast-stat-cell-tv${hideOnMobile ? ' is-hide-mobile' : ''}`}>
      <div className="bcast-stat-cell-label">{label}</div>
      <div
        className="bcast-stat-cell-value bcast-tnum"
        style={liveTint ? { color: 'var(--bcast-accent)' } : undefined}
      >
        {value}
      </div>
      {/* Mobile single-line variant. CSS hides this on desktop. */}
      <div
        className="bcast-stat-cell-mobile bcast-tnum"
        style={liveTint ? { color: 'var(--bcast-accent)' } : undefined}
      >
        {mobileText ?? value}
      </div>
    </div>
  );
}

// ── MatchRow ──────────────────────────────────────────────────────────────
function MatchRow({
  match, palettes, isMine, onClick,
}: {
  match: SectionMatch;
  palettes: { a: TeamPalette; b: TeamPalette };
  isMine: boolean;
  onClick: () => void;
}) {
  const sideA = (match.participants ?? []).filter((p) => p.sideNumber === 1);
  const sideB = (match.participants ?? []).filter((p) => p.sideNumber === 2);

  // Single source of truth across every surface that summarizes a
  // match. See lib/matchState.ts. The score-entry page uses the same
  // helper so the banner + team-row leader/margin can't drift.
  const state = deriveMatchState({
    teamAId: match.teamAId,
    teamBId: match.teamBId,
    status: match.status,
    winningTeamId: match.winningTeamId,
    finalResultType: match.finalResultType,
    finalResultDisplay: match.finalResultDisplay,
    matchHoles: match.matchHoles ?? [],
  });
  const { leader, marginLabel, played, isFinal, thruLabel } = state;

  const leftStyle: CSSProperties = leader === 'a'
    ? { background: palettes.a.soft, borderLeftColor: palettes.a.solid }
    : {};
  const rightStyle: CSSProperties = leader === 'b'
    ? { background: palettes.b.soft, borderRightColor: palettes.b.solid }
    : {};

  return (
    <button type="button" onClick={onClick} className="bcast-match-row">
      {/* Left side (Team A) */}
      <div className={`bcast-row-side red ${leader === 'a' ? 'is-leading' : ''}`} style={leftStyle}>
        <span className={`bcast-row-team-text ${leader === 'a' ? 'is-bold' : ''}`}>
          {sideA.map((p) => firstName(p.displayName)).join(' / ').toUpperCase() || 'TBD'}
        </span>
        {leader === 'a' && (
          <span className="bcast-row-margin" style={{ color: palettes.a.solid }}>{marginLabel}</span>
        )}
      </div>

      {/* Center status pill */}
      <div className="bcast-row-center">
        <span className={`bcast-row-thru ${isFinal ? 'is-final' : ''}`}>{thruLabel}</span>
        {isMine && (
          <span className="bcast-row-mine">● MINE</span>
        )}
      </div>

      {/* Right side (Team B) */}
      <div className={`bcast-row-side blue ${leader === 'b' ? 'is-leading' : ''}`} style={rightStyle}>
        <span className={`bcast-row-team-text ${leader === 'b' ? 'is-bold' : ''}`}>
          {sideB.map((p) => firstName(p.displayName)).join(' / ').toUpperCase() || 'TBD'}
        </span>
        {leader === 'b' && (
          <span className="bcast-row-margin" style={{ color: palettes.b.solid }}>{marginLabel}</span>
        )}
        {leader === 'tie' && played > 0 && (
          <span className="bcast-row-margin" style={{ color: 'var(--bcast-ink-3)' }}>AS</span>
        )}
      </div>
    </button>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function firstName(s: string): string {
  return (s ?? '').trim().split(/\s+/)[0] || s;
}

function formatSessionValue(round: BroadcastRoundSectionProps['round']): string {
  const format = formatLabel(round.formatType);
  return format;
}

function formatLabel(t: string): string {
  switch (t) {
    case 'singles_match_play':       return 'SINGLES';
    case 'four_ball_better_ball':    return 'FOUR-BALL';
    case 'foursomes_alternate_shot': return 'FOURSOMES';
    case 'scramble_match_play':      return 'SCRAMBLE';
    case 'shamble_match_play':       return 'SHAMBLE';
    case 'aggregate_match_play':     return 'AGGREGATE';
    case 'manual_result_only':       return 'MANUAL';
    default:                         return t.toUpperCase();
  }
}
